/**
 * The apps panel's chat: the Chief of Staff, the same conversation as the web app's dashboard and Slack, with the page
 * the user is on sent as context so record work ("add a milestone to this project") lands on the record on screen.
 * The Chief of Staff hands that work to the Operations Manager; nothing here talks to a records agent directly.
 */
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Alert, Box, Chip, Drawer, IconButton, Typography, Tooltip } from '@mui/material';
import { X, MessagesSquare, Upload } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useNode } from '@diggens/shared-hooks';
import { useCurrentUser } from '@diggens/user-management';
import { CONVERSATIONS_QUERY_KEY, ChatClient, ChatLinksProvider, ConversationList, type ChatClientRef } from '@diggens/chat';
import type { ChatAction, PageContext } from '@diggens/types';
import { useUIStore } from '../../stores/useUIStore';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';
import { useSchema } from '../../contexts';
import { derivePageContext, recordDisplayName } from '../../features/chat/pageContext';
import { CrmImportForm } from './CrmImportForm';

/** The CRM has no Activity page, so plan cards offer no link to one. */
const CRM_CHAT_LINKS = { planPath: null };

export function AgentPanel() {
  const { agentPanelWidth, setAgentPanelWidth, setAgentPanelOpen } = useUIStore();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<string | null>(null);
  const chatRef = useRef<ChatClientRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isDragging = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: user } = useCurrentUser();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { schema } = useSchema();

  // Where the user is, from the route and the tenant schema; the record's name comes from the record itself
  const place = useMemo(() => derivePageContext(location.pathname, schema), [location.pathname, schema]);
  const { data: record } = useNode(place.recordId);
  const recordType = place.objectType ? schema.nodeTypes[place.objectType] : undefined;
  const recordName = recordDisplayName(record?.props as Record<string, unknown> | undefined, recordType);
  // The user can drop the record from the context for the next messages; moving to another page brings it back
  const [recordDropped, setRecordDropped] = useState(false);
  useEffect(() => setRecordDropped(false), [location.pathname]);
  const pageContext: PageContext = useMemo(() => {
    if (recordDropped || !place.recordId) {
      const { recordId: _recordId, ...rest } = place;
      return rest;
    }
    return recordName ? { ...place, recordName } : place;
  }, [place, recordDropped, recordName]);

  // The web app renders the Chief of Staff's forms and status tables; here they are only announced
  const handleAction = (action: ChatAction | null) => {
    if (action?.type === 'show_form' || action?.type === 'show_status') {
      setNotice('Forms and status tables open in the Diggens web app. Ask your Chief of Staff there to fill one in, or carry on here in words.');
    }
  };

  const handleReplySaved = () => {
    queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY });
  };

  const openConversation = (id: string) => {
    setHistoryOpen(false);
    chatRef.current?.loadConversation(id);
  };

  const startNewConversation = () => {
    setHistoryOpen(false);
    chatRef.current?.startNewConversation();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.name.toLowerCase().endsWith('.csv')) {
      setImportFile(file);
      setImportResult(null);
    } else if (file) {
      setImportResult('Please choose a .csv file.');
    }
    event.target.value = '';
  };

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDragging.current = true;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging.current) return;
        const containerWidth = window.innerWidth;
        const newWidth = ((containerWidth - e.clientX) / containerWidth) * 100;
        setAgentPanelWidth(newWidth);
      };

      const handleMouseUp = () => {
        isDragging.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [setAgentPanelWidth]
  );

  const contextLabel = place.recordId && !recordDropped ? `${recordType?.displayName ?? place.objectType ?? 'Record'}: ${recordName ?? '…'}` : null;

  return (
    <Box
      ref={containerRef}
      sx={{
        width: `${agentPanelWidth}%`,
        minWidth: '300px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderLeft: 1,
        borderColor: 'divider',
        position: 'relative',
      }}
    >
      {/* Drag Handle */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          cursor: 'col-resize',
          bgcolor: 'transparent',
          '&:hover': {
            bgcolor: 'primary.main',
          },
          transition: 'background-color 0.2s',
          zIndex: 10,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6">Chief of Staff</Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileSelect} style={{ display: 'none' }} />
          <Tooltip title="Import records from a CSV file">
            <IconButton size="small" aria-label="Import CSV" onClick={() => fileInputRef.current?.click()}>
              <Upload size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Conversations">
            <IconButton size="small" aria-label="Conversations" onClick={() => setHistoryOpen(true)}>
              <MessagesSquare size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />
            </IconButton>
          </Tooltip>
          <IconButton size="small" aria-label="Close" onClick={() => setAgentPanelOpen(false)}>
            <X size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />
          </IconButton>
        </Box>
      </Box>

      {/* The record on screen, which the next messages are about unless dropped */}
      {contextLabel && (
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Context:
          </Typography>
          <Chip size="small" label={contextLabel} onDelete={() => setRecordDropped(true)} deleteIcon={<X size={14} />} sx={{ maxWidth: 280 }} />
        </Box>
      )}

      {notice && (
        <Alert severity="info" onClose={() => setNotice(null)} sx={{ mx: 2, mt: 1 }}>
          {notice}
        </Alert>
      )}

      {(importFile || importResult) && (
        <Box sx={{ px: 2, pt: 1 }}>
          {importResult && (
            <Alert severity="info" onClose={() => setImportResult(null)} sx={{ mb: 1 }}>
              {importResult}
            </Alert>
          )}
          {importFile && (
            <CrmImportForm
              file={importFile}
              onSuccess={(result) => {
                setImportFile(null);
                setImportResult(`${result.message}${result.skipped > 0 ? ` Skipped ${result.skipped} duplicate(s).` : ''}`);
              }}
              onCancel={() => setImportFile(null)}
            />
          )}
        </Box>
      )}

      {/* Chat */}
      <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
        {user && user.tenant_id && (
          <ChatLinksProvider links={CRM_CHAT_LINKS}>
            <ChatClient
              ref={chatRef}
              tenantId={user.tenant_id}
              userId={user.id}
              userName={(user.full_name || user.email || 'User') as string}
              onActionTriggered={handleAction}
              pageContext={pageContext}
              showHeader={false}
              uploads={false}
              onConversationChange={setConversationId}
              onReplySaved={handleReplySaved}
            />
          </ChatLinksProvider>
        )}
      </Box>

      <Drawer anchor="right" open={historyOpen} onClose={() => setHistoryOpen(false)}>
        <Box sx={{ width: 320, height: '100%' }}>
          <ConversationList currentConversationId={conversationId} onSelect={openConversation} onNew={startNewConversation} />
        </Box>
      </Drawer>
    </Box>
  );
}
